import { resetRouter, componentsList, defaultRouterList } from "../../router";
import local from "../../util/local";


/**
 * 根据接口返回的菜单列表（扁平结构，通过 parentId 关联），
 * 结合 components.js 中的路由配置（路径-页面映射），
 * 构建出用于左侧菜单显示的树形结构。
 *
 * @param {Array} menuList - 接口返回的菜单列表 [{storeMenuId, name, icon, level, url, parentId, sort}, ...]
 * @param {Array} routes - components.js 中的 asyncRouterList（路由配置）
 * @returns {Array} 树形菜单结构，用于左侧菜单渲染
 */
function buildMenuTreeFromApi(menuList, routes) {
  if (!menuList || menuList.length === 0) return [];

  // 1. 构建接口菜单的映射：storeMenuId -> item
  const menuMap = {};
  menuList.forEach((item) => {
    menuMap[item.storeMenuId] = { ...item };
  });

  // 2. 构建接口菜单的父子关系树（按 parentId 分组）
  const childrenMap = {}; // parentId -> [child, ...]
  menuList.forEach((item) => {
    const parentId = item.parentId || "0";
    if (!childrenMap[parentId]) {
      childrenMap[parentId] = [];
    }
    childrenMap[parentId].push(item);
  });

  // 3. 对每层的 children 按 sort 排序
  function sortChildren(parentId) {
    const children = childrenMap[parentId];
    if (children) {
      children.sort((a, b) => (a.sort || 0) - (b.sort || 0));
    }
  }
  // 对所有层级排序
  Object.keys(childrenMap).forEach((pid) => sortChildren(pid));

  // 4. 构建路由路径到路由配置的映射（用于查找 component）
  //    同时构建路径到 meta 信息的映射
  const pathRouteMap = {}; // fullPath -> route config
  const pathMetaMap = {}; // fullPath -> { title, icon }

  function buildPathMap(routes, parentPath = "") {
    routes.forEach((route) => {
      const fullPath = route.path.startsWith("/")
        ? route.path
        : `${parentPath}/${route.path}`.replace(/\/+/g, "/");

      pathRouteMap[fullPath] = route;

      if (route.meta) {
        pathMetaMap[fullPath] = {
          title: route.meta.title,
          icon: route.meta.icon,
        };
      }

      if (route.children) {
        buildPathMap(route.children, fullPath);
      }
    });
  }
  buildPathMap(routes);

  // 5. 递归构建菜单树
  function buildTree(parentId) {
    const children = childrenMap[parentId];
    if (!children) return [];

    return children.map((menuItem) => {
      // 对于没有 url 的菜单项（如一级分类菜单或暂无页面的菜单），
      // 如果有子菜单，则使用第一个子菜单的 url 作为默认跳转路径
      let path = menuItem.url || "";
      if (!path) {
        const subChildren = childrenMap[menuItem.storeMenuId];
        if (subChildren && subChildren.length > 0) {
          // 找第一个有 url 的子菜单
          const firstWithUrl = subChildren.find((child) => child.url);
          if (firstWithUrl) {
            path = firstWithUrl.url;
          }
        }
      }

      // 如果最终 path 仍为空（菜单项没有 url 且子菜单也没有 url），
      // 使用 storeMenuId 作为唯一标识，避免 el-submenu 的 index 冲突
      const uniqueKey = path || menuItem.storeMenuId;

      const node = {
        path: uniqueKey,
        title: menuItem.name,
        icon: menuItem.icon || "",
        meta: {
          title: menuItem.name,
          icon: menuItem.icon || "",
        },
        children: undefined,
      };


      // 如果有 url，尝试从路由配置中查找对应的 meta 信息（如 icon）
      if (menuItem.url && pathMetaMap[menuItem.url]) {
        const routeMeta = pathMetaMap[menuItem.url];
        // 优先使用接口返回的 icon，如果没有则使用路由配置中的 icon
        if (!menuItem.icon && routeMeta.icon) {
          node.icon = routeMeta.icon;
          node.meta.icon = routeMeta.icon;
        }
      }

      // 递归构建子菜单
      const subChildren = buildTree(menuItem.storeMenuId);
      if (subChildren.length > 0) {
        node.children = subChildren;
      }

      return node;
    });
  }


  // 从根节点（parentId === "0"）开始构建
  return buildTree("0");
}

/**
 * 从构建好的菜单树中提取所有有效的 URL 路径集合，
 * 用于路由权限判断（permission.js 中的 checkRouter）
 */
export function extractUrlsFromMenuTree(menuTree) {
  const urls = [];
  function walk(nodes) {
    nodes.forEach((node) => {
      if (node.path) {
        urls.push(node.path);
      }
      if (node.children) {
        walk(node.children);
      }
    });
  }
  walk(menuTree);
  return urls;
}

const state = {
  whiteListRouters: defaultRouterList
    .filter((item) => {
      return !item.meta.requireAuth && !item.hidden;
    })
    .map((i) => {
      return i.path;
    }),
  routers: [],
};

const mutations = {
  setRouters: (state, routers) => {
    state.routers = routers;
  },
};

const getters = {
  routers: (state) => state.routers,
  whiteListRouters: (state) => state.whiteListRouters,
};

const actions = {
  async initRoutes({ commit }, roles) {
    const staffMenuList = local.getStaffMenuList();

    // 使用接口返回的菜单数据构建树形菜单结构
    // componentsList 是原始的 asyncRouterList，用于提供路径-页面对应关系
    const menuTree = buildMenuTreeFromApi(staffMenuList, componentsList);

    commit("setRouters", menuTree);
    console.log("setRouters", menuTree);

    // 将构建好的菜单树中的 URL 提取出来，存储到 localStorage
    // 供 permission.js 中的 checkRouter 使用
    const urls = extractUrlsFromMenuTree(menuTree);
    // 同时也保留原始的 staffMenuList 用于路由守卫判断
    // 但我们需要更新 staffMenuList 中的 url 信息
    // 实际上 checkRouter 使用的是 local.getStaffMenuList() 中的 url
    // 所以我们需要确保 staffMenuList 中的 url 是完整的
    // 这里不需要额外操作，因为 staffMenuList 已经在登录时存储好了
  },
  async restore({ commit }) {
    // remove routers
    resetRouter();
    commit("setRouters", []);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};



// WEBPACK FOOTER //
// ./src/store/modules/permission.js